import type {
  AccessCatalogItemPublic,
  AccessRecordPublic,
  ApplicationPublic,
  SessionUser,
  UserPublic,
} from '@accounts/shared';
import { type CSSProperties, useCallback, useMemo, useState } from 'react';

import { STR } from '../../../i18n';
import { EmptyRow, EmptyScreen, ListTruncationNotice, Skeleton } from '../../../ui/Primitives';
import { monoStyle, primaryButtonStyle, searchFieldStyle } from '../../../ui/styles';

import { appStatusAt, indexAppStatus } from './appStatus';
import { groupRows, rowShort } from './catalog/hierarchy';
import {
  useAccessCatalogAll,
  useAccessLevels,
  useAccessNodesAll,
  useAccessRecordsAll,
  useApplicationsList,
  useLiveGrantsAll,
  useUsersList,
} from './catalog/queries';
import { EmptyCellDialog } from './matrix/EmptyCellDialog';
import { AppMatrixCell, MatrixCell } from './matrix/MatrixCell';
import { MatrixHeatGrid } from './matrix/MatrixHeatGrid';
import { MatrixShowSelect } from './matrix/MatrixShowSelect';
import {
  addFromEmpty,
  dialogForCell,
  indexRecords,
  levelOptions,
  type MatrixDialog,
  type MatrixFilters,
  matrixPeople,
  outsideRows,
  recordAt,
  rowMatches,
  withLevelPick,
} from './matrix/model';
import { useRoleQueue } from './queue/useRoleQueue';
import { AddRecordDialog, RecordDialog } from './RecordDialogs';

/**
 * THE MATRIX (FINAL demo R3-2, spec row 17): every catalog row against every person —
 * the suite apps first, then the outside systems filed by the admin's hierarchy. Three views,
 * as the board kept them (SIR-7657 restores the third):
 *   - Row view: a row per person; a Suite apps column group, then a column per outside
 *     catalog row grouped by top-level entry;
 *   - Table view: the same turned — a Suite apps row group, then a row per outside catalog
 *     row (grouped the same way), a column per person: the tracker sheet's orientation;
 *   - Heatmap (round-2 demo A3 heat grid): a square per top-level group × the entry below it, shaded
 *     by how many people have it, with a side list for the picked square
 *     (`matrix/MatrixHeatGrid.tsx`).
 * Filters: one per level (each narrowing the next), active people or everyone, Show (every
 * login, or only what needs work), and a person search. A filled outside cell opens the
 * record and its history; an empty one opens "No access recorded", whose Add access… opens
 * the add form with its What picker (suite apps become grants, outside systems records). A
 * suite-app cell is the person's grant state on that app (`appStatus.ts`): Granted, Asked (a
 * pending request), or a dot. Every cell takes the demo's hover fill.
 * The page header's workbook action downloads the complete server-built export. Matrix
 * filters are deliberately not sent: they shape only this screen, not the workbook of record.
 */

type View = 'rows' | 'table' | 'heat';

const VIEW_LABEL: Record<View, string> = {
  rows: STR.access.matrix.viewRows,
  table: STR.access.matrix.viewTable,
  heat: STR.access.matrix.viewHeat,
};

const headStyle: CSSProperties = {
  position: 'sticky',
  background: 'var(--surface-2)',
  borderRight: '1px solid var(--border)',
  borderBottom: '1px solid var(--border)',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const tdStyle: CSSProperties = {
  borderRight: '1px solid var(--border)',
  borderBottom: '1px solid var(--border)',
  padding: 0,
};

const rowHeadStyle: CSSProperties = {
  position: 'sticky',
  left: 0,
  zIndex: 2,
  background: 'var(--surface)',
  borderRight: '1px solid var(--border)',
  borderBottom: '1px solid var(--border)',
  textAlign: 'left',
  padding: '5px 12px',
  whiteSpace: 'nowrap',
  fontWeight: 600,
};

const groupSep = '2px solid var(--border-strong)';

/** A group's heading row in the table view. */
const groupHeadStyle: CSSProperties = {
  ...tdStyle,
  background: 'var(--surface-2)',
  textAlign: 'left',
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: '.07em',
  textTransform: 'uppercase',
  padding: '6px 12px',
};

export function MatrixPanel({ user }: { user: SessionUser }) {
  const levels = useAccessLevels();
  const nodes = useAccessNodesAll();
  const catalog = useAccessCatalogAll();
  const records = useAccessRecordsAll(catalog.data ?? []);
  const users = useUsersList();
  const applications = useApplicationsList();
  const apps = useMemo(
    () =>
      [...(applications.data?.data ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      ),
    [applications.data],
  );
  const grants = useLiveGrantsAll(apps);
  // Pending asks on app-bound rows: the Queue's request lane, the Queue's own cache entry.
  const queue = useRoleQueue(user);

  const [view, setView] = useState<View>('rows');
  const [filters, setFilters] = useState<MatrixFilters>({
    levels: {},
    people: 'active',
    search: '',
    show: 'all',
  });
  const [dialog, setDialog] = useState<MatrixDialog | null>(null);
  const closeDialog = useCallback(() => {
    setDialog(null);
  }, []);
  const openRecord = useCallback((record: AccessRecordPublic) => {
    setDialog({ kind: 'record', record });
  }, []);
  const openEmpty = useCallback((userId: string, catalogItemId: string) => {
    setDialog(dialogForCell(userId, catalogItemId, undefined));
  }, []);

  if (
    levels.isError ||
    nodes.isError ||
    catalog.isError ||
    records.isError ||
    users.isError ||
    applications.isError ||
    grants.isError ||
    queue.isError
  ) {
    return (
      <EmptyScreen
        title={STR.access.matrix.loadFailedTitle}
        body={STR.access.matrix.loadFailedBody}
        action={
          <button
            type="button"
            className="prim"
            style={primaryButtonStyle}
            onClick={() => {
              void levels.refetch();
              void nodes.refetch();
              void catalog.refetch();
              records.refetch();
              void users.refetch();
              void applications.refetch();
              grants.refetch();
              queue.refetch();
            }}
          >
            {STR.common.tryAgain}
          </button>
        }
      />
    );
  }

  if (
    levels.data === undefined ||
    nodes.data === undefined ||
    catalog.data === undefined ||
    records.data === undefined ||
    users.data === undefined ||
    applications.data === undefined ||
    grants.data === undefined ||
    queue.isLoading
  ) {
    return (
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Skeleton style={{ height: 32, width: 420, borderRadius: 8 }} />
        <Skeleton style={{ height: 320, width: '100%', borderRadius: 12 }} />
      </div>
    );
  }

  const levelList = levels.data;
  const levelsByDepth = [...levelList].sort((a, b) => a.depth - b.depth);
  const index = indexRecords(records.data);
  const rows = outsideRows(catalog.data).filter((item) => rowMatches(item, filters.levels));
  const groups = groupRows(rows);
  const columns = groups.flatMap(([, groupRowsList]) => groupRowsList);
  const firstOfGroup = new Set(
    groups
      .map(([, groupRowsList]) => groupRowsList[0]?.id)
      .filter((id): id is string => id !== undefined),
  );
  const people = matrixPeople(users.data.data, filters, columns, index);
  const appIndex = indexAppStatus(
    grants.data,
    queue.items.flatMap((item) =>
      item.type === 'request' && item.catalogItem.applicationId !== null
        ? [{ userId: item.user.id, applicationId: item.catalogItem.applicationId }]
        : [],
    ),
  );

  // A record changed elsewhere (or just now in its dialog) shows its CURRENT state here.
  const openRecordId = dialog?.kind === 'record' ? dialog.record.id : null;
  const liveDialogRecord =
    openRecordId === null
      ? undefined
      : (records.data.find((record) => record.id === openRecordId) ??
        (dialog?.kind === 'record' ? dialog.record : undefined));

  const emptyDialog = dialog?.kind === 'empty' ? dialog : null;
  const emptyPerson =
    emptyDialog === null ? undefined : users.data.data.find((u) => u.id === emptyDialog.userId);
  const emptyItem =
    emptyDialog === null
      ? undefined
      : catalog.data.find((item) => item.id === emptyDialog.catalogItemId);

  const cell = (person: UserPublic, item: AccessCatalogItemPublic) => (
    <MatrixCell
      person={person}
      item={item}
      record={recordAt(index, person.id, item.id)}
      quiet={filters.show === 'attention'}
      onOpen={openRecord}
      onEmpty={openEmpty}
    />
  );

  const appCell = (person: UserPublic, application: ApplicationPublic) => (
    <AppMatrixCell
      person={person}
      application={application}
      status={appStatusAt(appIndex, person.id, application.id)}
    />
  );

  const personHead = (person: UserPublic) => (
    <>
      <div style={{ fontSize: 12, fontWeight: 650 }}>{person.name}</div>
      {person.active ? null : (
        <div className="tx-subtle" style={{ fontSize: 10.5, fontWeight: 500 }}>
          {STR.access.matrix.deactivated}
        </div>
      )}
    </>
  );

  const selectStyle: CSSProperties = {
    padding: '6px 28px 6px 10px',
    borderRadius: 8,
    fontSize: 12.5,
  };

  return (
    <div
      style={{
        padding: '20px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        height: '100%',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <div
          role="group"
          aria-label={STR.access.matrix.viewLabel}
          style={{ display: 'flex', gap: 2, padding: 3, borderRadius: 9, border: '1px solid var(--border)' }}
        >
          {(['rows', 'table', 'heat'] as const).map((option) => (
            <button
              key={option}
              type="button"
              className="seg"
              aria-pressed={view === option}
              onClick={() => {
                setView(option);
              }}
              style={{ padding: '6px 10px', borderRadius: 6, fontSize: 12, fontWeight: 650, cursor: 'pointer' }}
            >
              {VIEW_LABEL[option]}
            </button>
          ))}
        </div>
        {levelList.map((level) => {
          const options = levelOptions(level, levelList, nodes.data, filters.levels);
          return (
            <select
              key={level.id}
              className="field"
              aria-label={STR.access.matrix.filterLabel(level.name)}
              value={filters.levels[level.id] ?? ''}
              onChange={(event) => {
                const nodeId = event.target.value;
                setFilters((state) => ({
                  ...state,
                  levels: withLevelPick(levelList, state.levels, level.id, nodeId),
                }));
              }}
              style={selectStyle}
            >
              <option value="">{STR.access.matrix.every(level.name)}</option>
              {options.map((node) => (
                <option key={node.id} value={node.id}>
                  {node.name}
                </option>
              ))}
            </select>
          );
        })}
        <select
          className="field"
          aria-label={STR.access.matrix.peopleLabel}
          value={filters.people}
          onChange={(event) => {
            const scope = event.target.value === 'all' ? 'all' : 'active';
            setFilters((state) => ({ ...state, people: scope }));
          }}
          style={selectStyle}
        >
          <option value="active">{STR.access.matrix.peopleActive}</option>
          <option value="all">{STR.access.matrix.peopleAll}</option>
        </select>
        <MatrixShowSelect
          value={filters.show}
          onChange={(show) => {
            setFilters((state) => ({ ...state, show }));
          }}
          style={selectStyle}
        />
        <input
          className="field"
          type="search"
          value={filters.search}
          placeholder={STR.access.matrix.searchPlaceholder}
          aria-label={STR.access.matrix.searchLabel}
          onChange={(event) => {
            const search = event.target.value;
            setFilters((state) => ({ ...state, search }));
          }}
          style={{ ...searchFieldStyle, width: 180 }}
        />
      </div>

      <div
        className="tx-subtle"
        style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', fontSize: 11.5 }}
      >
        <span>
          <span style={{ ...monoStyle, color: 'var(--text)' }}>{STR.access.matrix.legendLogin}</span>{' '}
          {STR.access.matrix.legendActive}
        </span>
        <span>
          <b className="tx-warn-strong">{STR.accessCommon.recordCell.needs_access}</b>{' '}
          {STR.access.matrix.legendNeeds}
        </span>
        <span>
          <b className="tx-subtle">{STR.accessCommon.recordCell.requested}</b>{' '}
          {STR.access.matrix.legendRequested}
        </span>
        <span>
          <b className="tx-danger-strong">{STR.accessCommon.recordCell.remove_access}</b>{' '}
          {STR.access.matrix.legendRemove}
        </span>
        <span>{STR.access.matrix.legendShared}</span>
        <span>{STR.access.matrix.legendClick}</span>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          border: '1px solid var(--border)',
          borderRadius: 12,
          background: 'var(--surface)',
        }}
      >
        {columns.length === 0 && apps.length === 0 ? (
          <EmptyRow>{STR.access.matrix.noRows}</EmptyRow>
        ) : people.length === 0 ? (
          <EmptyRow>{STR.access.matrix.nobodyMatches}</EmptyRow>
        ) : view === 'heat' ? (
          <MatrixHeatGrid
            groups={groups}
            apps={apps}
            people={people}
            index={index}
            appIndex={appIndex}
            quiet={filters.show === 'attention'}
            topLevel={levelsByDepth[0]?.name}
            nextLevel={levelsByDepth[1]?.name}
            onOpen={openRecord}
            onEmpty={openEmpty}
          />
        ) : view === 'rows' ? (
          <table style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: 11.5 }}>
            <thead>
              <tr>
                <th
                  rowSpan={2}
                  style={{
                    ...headStyle,
                    top: 0,
                    left: 0,
                    zIndex: 4,
                    minWidth: 200,
                    verticalAlign: 'bottom',
                    padding: '8px 12px',
                  }}
                >
                  {STR.access.matrix.colPerson}
                </th>
                {apps.length === 0 ? null : (
                  <th
                    colSpan={apps.length}
                    style={{ ...headStyle, top: 0, zIndex: 3, height: 30, padding: '6px 10px', borderLeft: groupSep }}
                  >
                    {STR.access.matrix.suiteApps}
                  </th>
                )}
                {groups.map(([group, groupRowsList]) => (
                  <th
                    key={group}
                    colSpan={groupRowsList.length}
                    style={{ ...headStyle, top: 0, zIndex: 3, height: 30, padding: '6px 10px', borderLeft: groupSep }}
                  >
                    {group}
                  </th>
                ))}
              </tr>
              <tr>
                {apps.map((application, at) => (
                  <th
                    key={application.id}
                    title={application.name}
                    style={{
                      ...headStyle,
                      top: 30,
                      zIndex: 3,
                      padding: '5px 8px',
                      ...(at === 0 ? { borderLeft: groupSep } : {}),
                    }}
                  >
                    {application.name}
                  </th>
                ))}
                {columns.map((item) => (
                  <th
                    key={item.id}
                    title={item.name}
                    style={{
                      ...headStyle,
                      top: 30,
                      zIndex: 3,
                      padding: '5px 8px',
                      ...(firstOfGroup.has(item.id) ? { borderLeft: groupSep } : {}),
                    }}
                  >
                    {rowShort(item)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {people.map((person) => (
                <tr key={person.id}>
                  <th scope="row" style={rowHeadStyle}>
                    {personHead(person)}
                  </th>
                  {apps.map((application, at) => (
                    <td
                      key={application.id}
                      style={{ ...tdStyle, ...(at === 0 ? { borderLeft: groupSep } : {}) }}
                    >
                      {appCell(person, application)}
                    </td>
                  ))}
                  {columns.map((item) => (
                    <td
                      key={item.id}
                      style={{ ...tdStyle, ...(firstOfGroup.has(item.id) ? { borderLeft: groupSep } : {}) }}
                    >
                      {cell(person, item)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: 11.5 }}>
            <thead>
              <tr>
                <th style={{ ...headStyle, top: 0, left: 0, zIndex: 4, minWidth: 220, padding: '8px 12px' }}>
                  {STR.access.matrix.colRow}
                </th>
                {people.map((person) => (
                  <th
                    key={person.id}
                    scope="col"
                    style={{ ...headStyle, top: 0, zIndex: 3, padding: '6px 8px', maxWidth: 140 }}
                  >
                    {personHead(person)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {apps.length === 0
                ? null
                : [
                    <tr key="group-suite-apps">
                      <th colSpan={people.length + 1} scope="colgroup" className="tx-subtle" style={groupHeadStyle}>
                        {STR.access.matrix.suiteApps}
                      </th>
                    </tr>,
                    ...apps.map((application) => (
                      <tr key={application.id}>
                        <th scope="row" title={application.name} style={rowHeadStyle}>
                          <span style={{ fontSize: 12 }}>{application.name}</span>
                        </th>
                        {people.map((person) => (
                          <td key={person.id} style={tdStyle}>
                            {appCell(person, application)}
                          </td>
                        ))}
                      </tr>
                    )),
                  ]}
              {groups.map(([group, groupRowsList]) => [
                <tr key={`group-${group}`}>
                  <th colSpan={people.length + 1} scope="colgroup" className="tx-subtle" style={groupHeadStyle}>
                    {group}
                  </th>
                </tr>,
                ...groupRowsList.map((item) => (
                  <tr key={item.id}>
                    <th scope="row" title={item.name} style={rowHeadStyle}>
                      <span style={{ fontSize: 12 }}>{rowShort(item)}</span>
                    </th>
                    {people.map((person) => (
                      <td key={person.id} style={tdStyle}>
                        {cell(person, item)}
                      </td>
                    ))}
                  </tr>
                )),
              ])}
            </tbody>
          </table>
        )}
      </div>

      <div
        className="tx-subtle"
        style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
      >
        {STR.access.matrix.counts(people.length, apps.length, columns.length, groups.length)}
      </div>
      <ListTruncationNotice loaded={users.data.data.length} total={users.data.meta.total} />

      {dialog?.kind === 'record' && liveDialogRecord !== undefined ? (
        <RecordDialog record={liveDialogRecord} onClose={closeDialog} />
      ) : null}
      {emptyDialog !== null && emptyPerson !== undefined && emptyItem !== undefined ? (
        <EmptyCellDialog
          person={emptyPerson}
          item={emptyItem}
          onClose={closeDialog}
          onAdd={() => {
            setDialog(addFromEmpty(emptyDialog));
          }}
        />
      ) : null}
      {dialog?.kind === 'add' ? (
        <AddRecordDialog
          userId={dialog.userId}
          catalogItemId={dialog.catalogItemId}
          withSuiteApps
          onClose={closeDialog}
        />
      ) : null}
    </div>
  );
}
